import { request, getCsrfHeaders, cookieAuthEnabled } from '@/utils/request';
import { establishSession, setUserInfo, logout } from '@/store/modules/user';
import { message } from 'antd';
import authSession from '@/utils/authSession';
import { getRequestErrorMessage, getRequestFailureMessage, getServerErrorMessage } from '@/utils/requestError';
import { accountStatusMessage } from '@/utils/accountModeration';

// 登录请求
const fetchLogin = (loginForm) => {
  return async (dispatch) => {
    try {
      const headers = await getCsrfHeaders();
      const res = await request.post('/user/login/', loginForm, { headers });
      // 请求成功则修改token
      if (res.status === 200) {
        dispatch(establishSession(res.data));
        return { success: true };
      }
      // 请求失败返回错误
      return { success: false, error: '登录失败' };

    } catch (error) {
      // 请求出错处理异常
      const failureMessage = getRequestFailureMessage(error);
      if (failureMessage) return { success: false, error: failureMessage };

      // 根据不同状态码返回不同的错误信息
      let errorMessage = '登录失败，请稍后重试';
      const errorData = error.response?.data;
      const serverMessage = getServerErrorMessage(error);
      // 默认认证错误同时涵盖密码错误和冻结账号，不能据此断定账号已冻结。
      const ambiguousCredentials = !serverMessage
        || errorData?.code === 'no_active_account'
        || /no active account found with the given credentials/i.test(serverMessage);

      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = accountStatusMessage(errorData) || (ambiguousCredentials
                ? '用户名或密码错误，或账号已被冻结，请联系管理员确认'
                : serverMessage);
            break;
          case 404:
            errorMessage = '登录服务暂不可用，请稍后重试';
            break;
          case 400:
            errorMessage = serverMessage || '请求参数错误';
            break;
          case 403:
            errorMessage = serverMessage || (errorData?.code === 'account_frozen'
              ? '账号已被冻结，请联系管理员解冻'
              : '登录被拒绝（403），请检查 CSRF Cookie 是否已被浏览器保存');
            break;
          case 429:
            errorMessage = getRequestErrorMessage(error);
            break;
          default:
            errorMessage = serverMessage || `登录失败 (${error.response.status})`;
        }
      }

      return { success: false, error: errorMessage };
    }
  };
};
// 退出登录：Cookie 模式下先请求后端吊销并清除 HttpOnly refresh cookie。
const fetchLogout = () => {
  return async dispatch => {
    let serverLogoutSucceeded = true;
    let logoutError = '';

    try {
      if (cookieAuthEnabled) {
        const headers = await getCsrfHeaders();
        await request.post('/user/logout/', {}, { headers });
      }
    } catch (error) {
      serverLogoutSucceeded = false;
      logoutError = getRequestErrorMessage(error, '退出登录失败，请稍后重试');
    } finally {
      dispatch(logout());
    }

    return serverLogoutSucceeded ? { success: true } : { success: false, error: logoutError };
  };
};
// 注册请求
const fetchRegister = async (registerForm) => {
  try {
    // 1. 发起注册请求
    const res = await request.post('/user/register/', registerForm)

    // 2. 返回成功结果（给调用页面用）
    return {
      success: true,
      data: res.data,
      message: '注册成功'
    }

  } catch (error) {

    // 3. 错误处理（根据后端返回精准提示）
    let errMsg = getRequestErrorMessage(error, '注册失败，请稍后重试');
    if (error.response?.status === 400) errMsg = getServerErrorMessage(error) || '参数填写错误';
    if (error.response?.status === 409) errMsg = '用户名已存在';

    // 4. 返回失败结果
    return {
      success: false,
      error: errMsg
    }
  }
}
// 修改密码请求
const fetchModify = async (modifyForm) => {
  await request.post('/user/change-password/', modifyForm);
}
// 获取用户信息请求
const fetchUserInfo = () => {
  return async (dispatch) => {
    const generation = authSession.getGeneration();
    try {
      // 1. 发起请求
      const res = await request.get('/user/me/');
      if (authSession.getGeneration() !== generation) return;

      // 2. 成功 → 保存到 redux
      dispatch(setUserInfo(res.data));

    } catch (err) {
      if (authSession.getGeneration() !== generation) return;

      // 3. 错误处理（token过期/未登录/服务器错误）
      if (err.response?.status === 401) {
        message.warning(getRequestErrorMessage(err));
        // 这里可以加清空token + 跳登录页
      } else {
        message.error(getRequestErrorMessage(err, '获取用户信息失败，请稍后重试'));
      }
    }
  };
}
// 修改用户信息请求
const fetchModifyUserInfo = async (modifyUserInfoForm, username) => {
  try {
    const res = await request.patch('/user/me/', {
      username,
      ...modifyUserInfoForm
    });

    // 返回成功结果
    return {
      success: true,
      data: res.data,
      message: '修改成功'
    };

  } catch (error) {

    const errMsg = getRequestErrorMessage(error, '修改失败，请稍后重试');

    return {
      success: false,
      error: errMsg
    };
  }
};

export default fetchModifyUserInfo;

export { fetchLogin, fetchLogout, fetchRegister, fetchModify, fetchUserInfo, fetchModifyUserInfo };
